import { useCallback, useEffect, useRef, useState } from "react";
import { useUiMode } from '@/hooks/useUiMode';
import { PRO_ONLY_PLACES } from '@/lib/uiMode.core';
import { raiseLiveNudge } from "@/components/orbLive";
import { track } from "@/lib/analytics";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { isViewingAsSomeoneElse } from "@/lib/viewAs";
import { GUIDES, ORB_GUIDE_EVENT, type TourStep } from "@/components/orbGuides";
import { afterTutorialVideo, QUICK_LEARN_FIRST_EVENT } from "@/components/TutorialVideoDialog";
import { onboardingEnabled, whenFirstVisitDecided } from "@/lib/onboarding";

/** Off since 2026-10-21 (engagement redesign); started from the header. */
const AUTO_TOUR = false;

/****************
 * Quick Learn — guided spotlight tour of the whole platform.
 *
 * A dimmed overlay punches a soft-glowing hole over one element at a
 * time while the CopyFi orb narrates what it does. Sections follow the
 * product funnel: find a trader → judge them → set your rules → go
 * live → read the results.
 *
 * Targets are plain [data-tour="…"] attributes on the pages — no page
 * logic changes. If a target is missing (empty state, still loading,
 * admin-gated) the step skips itself after a short poll, so the tour
 * can never strand the user on a dark screen.
 *
 * Runs automatically once per user (localStorage flag keyed by email)
 * after first sign-in, and on demand from the Quick Learn header
 * button, which dispatches QUICK_LEARN_EVENT.
 *
 * ── It also answers the Orb (2026-09-23) ───────────────────────────
 *
 * The same overlay now runs SHORT sequences on request: the Orb answers
 * a question, offers "Show me", and that dispatches ORB_GUIDE_EVENT with
 * the name of a guide from orbGuides.ts. Three steps at most, ending on
 * the control the person actually has to press.
 *
 * Which is why the running sequence is state rather than the STEPS
 * constant. Everything below — the polling, the navigation, the skip,
 * the card placement — works the same on either, and this is the whole
 * of the change: a tour that can start somewhere other than step 0.
 *
 * One deliberate asymmetry: a guide run does NOT set the seen flag.
 * Answering one question must not consume somebody's one automatic
 * tour, which they have not been shown yet.
 ****************/

export const QUICK_LEARN_EVENT = "copyfi:quicklearn:start";
const SEEN_PREFIX = "copyfi_quicklearn_seen:";

const STEPS: TourStep[] = [
    {
        sec: "tour.sec.welcome", route: "/", target: null, next: "tour.step1.next",
        title: "tour.step1.title",
        body: "tour.step1.body",
    },

    /* ── Dashboard ── */
    {
        sec: "tour.sec.dashboard", route: "/", target: "nav-dashboard",
        title: "tour.step2.title",
        body: "tour.step2.body",
    },
    {
        sec: "tour.sec.dashboard", route: "/", target: "dash-kpis",
        title: "tour.step3.title",
        body: "tour.step3.body",
    },
    {
        sec: "tour.sec.dashboard", route: "/", target: "dash-leaderboard",
        title: "tour.step4.title",
        body: "tour.step4.body",
    },
    {
        sec: "tour.sec.dashboard", route: "/", target: "dash-prices",
        title: "tour.step5.title",
        body: "tour.step5.body",
    },

    /* ── Traders ── */
    {
        sec: "tour.sec.traders", route: "/traders", target: "nav-traders",
        title: "tour.step6.title",
        body: "tour.step6.body",
    },
    {
        sec: "tour.sec.traders", route: "/traders", target: "tr-mode",
        title: "tour.step7.title",
        body: "tour.step7.body",
    },
    {
        sec: "tour.sec.traders", route: "/traders", target: "tr-charts",
        title: "tour.step8.title",
        body: "tour.step8.body",
    },
    {
        sec: "tour.sec.traders", route: "/traders", target: "tr-select",
        title: "tour.step9.title",
        body: "tour.step9.body",
    },

    /* ── Strategy Builder ── */
    {
        sec: "tour.sec.strategyBuilder", route: "/configure", target: "nav-strategy",
        title: "tour.step10.title",
        body: "tour.step10.body",
    },
    {
        sec: "tour.sec.strategyBuilder", route: "/configure", target: "sb-steps",
        title: "tour.step11.title",
        body: "tour.step11.body",
    },
    {
        sec: "tour.sec.strategyBuilder", route: "/configure", target: "sb-content",
        title: "tour.step12.title",
        body: "tour.step12.body",
    },

    /* ── Copy Trade ── */
    {
        sec: "tour.sec.copyTrade", route: "/copy-trade", target: "nav-sessions",
        title: "tour.step13.title",
        body: "tour.step13.body",
    },
    {
        sec: "tour.sec.copyTrade", route: "/copy-trade", target: "ct-tabs",
        title: "tour.step14.title",
        body: "tour.step14.body",
    },
    {
        sec: "tour.sec.copyTrade", route: "/copy-trade", target: "ct-config",
        title: "tour.step15.title",
        body: "tour.step15.body",
    },

    /* ── My Sessions ── */
    {
        sec: "tour.sec.mySessions", route: "/sessions", target: "nav-sessions",
        title: "tour.step16.title",
        body: "tour.step16.body",
    },
    {
        sec: "tour.sec.mySessions", route: "/sessions", target: "ss-table",
        title: "tour.step17.title",
        body: "tour.step17.body",
    },

    /* ── Learn ── */
    /* ── AI and Community (round 10 #413: the tour had neither) ── */
    {
        sec: "tour.sec.ai", route: "/ai", target: "nav-ai",
        title: "tour.stepAi.title",
        body: "tour.stepAi.body",
    },
    {
        sec: "tour.sec.community", route: "/community", target: "nav-community",
        title: "tour.stepCommunity.title",
        body: "tour.stepCommunity.body",
    },
    {
        sec: "tour.sec.learn", route: "/learn", target: "nav-learn",
        title: "tour.step18.title",
        body: "tour.step18.body",
    },

    /* ── Finish ── */
    {
        sec: "tour.sec.anytime", route: "/learn", target: "quick-learn", next: "tour.step19.next",
        title: "tour.step19.title",
        body: "tour.step19.body",
    },
];

/** Lite has no AI, Community or market sections: the tour skips them there (round 10 #413). */
const LITE_SKIP_TARGETS = ['dash-leaderboard', 'dash-prices'];
function stepsFor(mode: string): TourStep[] {
  if (mode !== 'lite') return STEPS;
  return STEPS.filter((s) => !(PRO_ONLY_PLACES as readonly string[]).includes(s.route) && !LITE_SKIP_TARGETS.includes(s.target ?? ''));
}

const SPOT_PAD = 8;
const CARD_W = 360;
const CARD_GAP = 24;
const MARGIN = 16;

interface SpotRect { top: number; left: number; width: number; height: number }

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

export function QuickLearnTour() {
    const [idx, setIdx] = useState(-1);
    const [spot, setSpot] = useState<SpotRect | null>(null);
    const [card, setCard] = useState({ x: 0, y: 0 });
    const [cardVisible, setCardVisible] = useState(false);
    /*
     * What is running. The full tour by default; a short Orb guide when one is
     * asked for. `isGuide` is not cosmetic — it decides whether finishing marks
     * the tour as seen.
     */
  const { mode: uiMode } = useUiMode();
    const [steps, setSteps] = useState<TourStep[]>(() => stepsFor(uiMode));
    const [isGuide, setIsGuide] = useState(false);

    const coachRef = useRef<HTMLDivElement>(null);
    const targetElRef = useRef<HTMLElement | null>(null);
    const dirRef = useRef(1);
    const autoStartedRef = useRef(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { t } = useTranslation("learn");
    // STEPS hold keys in the learn namespace. Orb guides (orbGuides.ts) may
    // still carry plain English; a string that is not a key is shown as is.
    const tr = (s: string) => t(s, { nsSeparator: false, defaultValue: s });

    const running = idx >= 0 && idx < steps.length;
    const step = running ? steps[idx] : null;
    const seenKey = SEEN_PREFIX + (user?.email || "anon");
    const reduced =
        typeof window !== "undefined" &&
        !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const idxRef = useRef(-1);
    idxRef.current = idx;
    useEffect(() => {
        if (idx === 0 && !isGuide) track("quicklearn_shown", undefined, undefined, { slide_index: 0 });
    }, [idx, isGuide]);
    const endTour = useCallback(() => {
        if (!isGuide && idxRef.current >= 0) {
            const last = idxRef.current >= steps.length - 1;
            track(last ? "quicklearn_completed" : "quicklearn_dismissed", undefined, undefined, last ? undefined : { slide_index: idxRef.current });
            // The tour explains the screen; the Orb takes over for the doing (Guillaume 2026-10-24, "symbiosis").
            if (last) window.setTimeout(() => raiseLiveNudge({ trigger: 'after-quicklearn', messageKey: 'orb.live.afterTour.message', bubbleKey: 'orb.live.afterTour.bubble', guide: 'first-steps', open: true }), 600);
        }
        setIdx(-1);
        setSpot(null);
        setCardVisible(false);
        targetElRef.current = null;
        /*
         * Only the real tour counts as "seen". Someone who asked the Orb one
         * question and was walked to one button has not been shown the product,
         * and silently spending their one automatic tour on that would take
         * away the thing it was saved for.
         */
        if (!isGuide) {
            try { localStorage.setItem(seenKey, "1"); } catch { /* private mode */ }
        }
    }, [seenKey, isGuide, steps.length]);

    const goTo = useCallback((next: number, dir: number) => {
        dirRef.current = dir;
        if (next < 0) return;
        if (next >= steps.length) { endTour(); return; }
        setIdx(next);
    }, [endTour, steps.length]);

    /* Card placement around the spotlight: right → below → above → left → bottom-center. */
    const placeCard = useCallback((r: DOMRect) => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const ch = coachRef.current?.offsetHeight || 250;
        const cw = Math.min(CARD_W, vw - 24);
        let x: number, y: number;
        if (r.right + CARD_GAP + cw <= vw - MARGIN) {
            x = r.right + CARD_GAP;
            y = clamp(r.top - 6, MARGIN + 40, vh - ch - MARGIN);
        } else if (r.bottom + CARD_GAP + ch <= vh - MARGIN) {
            y = r.bottom + CARD_GAP;
            x = clamp(r.left, MARGIN + 26, vw - cw - MARGIN);
        } else if (r.top - CARD_GAP - ch >= MARGIN + 30) {
            y = r.top - CARD_GAP - ch;
            x = clamp(r.left, MARGIN + 26, vw - cw - MARGIN);
        } else if (r.left - CARD_GAP - cw >= MARGIN) {
            x = r.left - CARD_GAP - cw;
            y = clamp(r.top - 6, MARGIN + 40, vh - ch - MARGIN);
        } else {
            x = vw / 2 - cw / 2;
            y = vh - ch - MARGIN;
        }
        setCard({ x: Math.round(x), y: Math.round(y) });
    }, []);

    const measure = useCallback((el: HTMLElement) => {
        targetElRef.current = el;
        const r = el.getBoundingClientRect();
        setSpot({
            top: r.top - SPOT_PAD,
            left: r.left - SPOT_PAD,
            width: r.width + SPOT_PAD * 2,
            height: r.height + SPOT_PAD * 2,
        });
        placeCard(r);
        setCardVisible(true);
        /* Second pass once the card has its real height for this step's copy. */
        requestAnimationFrame(() => {
            const el2 = targetElRef.current;
            if (el2) placeCard(el2.getBoundingClientRect());
        });
    }, [placeCard]);

    const placeCentered = useCallback(() => {
        targetElRef.current = null;
        setSpot(null);
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const ch = coachRef.current?.offsetHeight || 260;
        const cw = Math.min(CARD_W + 60, vw - 24);
        setCard({ x: Math.round(vw / 2 - cw / 2), y: Math.round(Math.max(24, vh / 2 - ch / 2)) });
        setCardVisible(true);
    }, []);

    /* Locate + spotlight the current step's target, navigating first if needed. */
    useEffect(() => {
        if (!running || !step) return;
        setCardVisible(false);
        if (location.pathname !== step.route) navigate(step.route);

        let cancelled = false;
        let attempts = 0;
        let timer: number | undefined;

        const tryLocate = () => {
            if (cancelled) return;
            if (!step.target) { placeCentered(); return; }
            const el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
            const visible = el && el.offsetWidth > 4 && el.offsetHeight > 4;
            if (!el || !visible) {
                attempts += 1;
                if (attempts <= 25) {
                    timer = window.setTimeout(tryLocate, 120);
                } else {
                    goTo(idx + dirRef.current, dirRef.current);
                }
                return;
            }
            const r = el.getBoundingClientRect();
            const needsScroll = r.top < 80 || r.bottom > window.innerHeight - 32;
            if (needsScroll) {
                el.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
                timer = window.setTimeout(() => { if (!cancelled) measure(el); }, reduced ? 40 : 420);
            } else {
                measure(el);
            }
        };

        /* Give a route change a beat to mount before the first probe. */
        timer = window.setTimeout(tryLocate, location.pathname !== step.route ? 220 : 30);
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx]);

    /* Keep the spotlight glued to its element through resize and scroll. */
    useEffect(() => {
        if (!running) return;
        let raf = 0;
        const refresh = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const el = targetElRef.current;
                if (el) {
                    const r = el.getBoundingClientRect();
                    setSpot({
                        top: r.top - SPOT_PAD,
                        left: r.left - SPOT_PAD,
                        width: r.width + SPOT_PAD * 2,
                        height: r.height + SPOT_PAD * 2,
                    });
                    placeCard(r);
                } else {
                    placeCentered();
                }
            });
        };
        window.addEventListener("resize", refresh);
        window.addEventListener("scroll", refresh, true);
        return () => {
            window.removeEventListener("resize", refresh);
            window.removeEventListener("scroll", refresh, true);
            cancelAnimationFrame(raf);
        };
    }, [running, placeCard, placeCentered]);

    /* Keyboard: Esc quits, arrows navigate. */
    useEffect(() => {
        if (!running) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") endTour();
            else if (e.key === "ArrowRight" || e.key === "Enter") goTo(idx + 1, 1);
            else if (e.key === "ArrowLeft" && idx > 0) goTo(idx - 1, -1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [running, idx, goTo, endTour]);

    /* On-demand start from the header button. Resets to the full tour, because
       the previous run may have been a two-step Orb guide and the button says
       Quick Learn, not "whatever you were shown last". */
    useEffect(() => {
        const onStart = () => {
            dirRef.current = 1;
            setSteps(stepsFor(uiMode));
            setIsGuide(false);
            setIdx(0);
        };
        window.addEventListener(QUICK_LEARN_EVENT, onStart);
        return () => window.removeEventListener(QUICK_LEARN_EVENT, onStart);
    }, []);

    /****************
     * The Orb asking for one thing to be shown.
     *
     * The name is looked up here rather than trusted: whatever arrives that is
     * not a key of GUIDES is ignored in silence. The backend validates it too,
     * against its own copy of the list — this is the second of the two, and it
     * is the one that decides what actually runs. An unknown name means a guide
     * was renamed in one place and not the other, and the right behaviour then
     * is to do nothing rather than to dim the screen over nothing.
     ****************/
    useEffect(() => {
        const onGuide = (e: Event) => {
            const name = (e as CustomEvent<{ guide?: string }>).detail?.guide;
            if (!name) return;
            const seq = Object.prototype.hasOwnProperty.call(GUIDES, name) ? GUIDES[name] : null;
            if (!seq || seq.length === 0) return;
            dirRef.current = 1;
            setSteps(seq);
            setIsGuide(true);
            setIdx(0);
        };
        window.addEventListener(ORB_GUIDE_EVENT, onGuide);
        return () => window.removeEventListener(ORB_GUIDE_EVENT, onGuide);
    }, []);

    /* First sign-in: fire once, automatically. */
    useEffect(() => {
        // Whether THEY have seen it lives in their browser, not this one: an
        // admin viewing their screen must not be shown a tour they may never see.
        if (autoStartedRef.current || !user?.email || isViewingAsSomeoneElse()) return;
        autoStartedRef.current = true;
        let seen: string | null = "1";
        try { seen = localStorage.getItem(seenKey); } catch { seen = "1"; }
        // No automatic start since 2026-10-21: Get Started is the first screen;
        // the tour stays in the header.
        if (!seen && AUTO_TOUR) {
            // A new account is shown the tutorial video first; the tour follows it.
            let cancelWait = () => {};
            let cancelDecide = () => {};
            const t = setTimeout(() => {
                const go = () => { cancelWait = afterTutorialVideo(() => { dirRef.current = 1; setIdx(0); }); };
                // With the welcome chooser on, a new account's first screen is the
                // chooser's (lib/onboarding.ts): the tour comes after the general
                // video, from QUICK_LEARN_FIRST_EVENT below, and never for the goals.
                if (onboardingEnabled()) cancelDecide = whenFirstVisitDecided((claimed) => { if (!claimed) go(); });
                else go();
            }, 1200);
            return () => { clearTimeout(t); cancelWait(); cancelDecide(); };
        }
    }, [user?.email, seenKey]);

    /* After the general video of the welcome chooser: the tour, if this browser has not seen it. */
    useEffect(() => {
        const onFirst = () => {
            let seen: string | null = "1";
            try { seen = localStorage.getItem(seenKey); } catch { seen = "1"; }
            if (seen) return;
            dirRef.current = 1;
            setSteps(stepsFor(uiMode));
            setIsGuide(false);
            setIdx(0);
        };
        window.addEventListener(QUICK_LEARN_FIRST_EVENT, onFirst);
        return () => window.removeEventListener(QUICK_LEARN_FIRST_EVENT, onFirst);
    }, [seenKey]);

    if (!running || !step) return null;

    const siblings = steps.reduce<number[]>((acc, s, j) => {
        if (s.sec === step.sec) acc.push(j);
        return acc;
    }, []);
    const secPos = siblings.indexOf(idx) + 1;
    const isCentered = !step.target;

    return (
        <>
            <style>{TOUR_CSS}</style>

            {spot ? (
                <div
                    className="ql-spot"
                    style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
                />
            ) : (
                <div className="ql-dim" />
            )}

            {/* Transparent click-catcher: a click on the dark advances. */}
            <div className="ql-catch" onClick={() => goTo(idx + 1, 1)} />

            <div
                ref={coachRef}
                className={`ql-coach${isCentered ? " ql-center" : ""}`}
                style={{ transform: `translate(${card.x}px, ${card.y}px)`, opacity: cardVisible ? 1 : 0 }}
                role="dialog"
                aria-modal="true"
                aria-label={t("tour.ariaLabel")}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ql-panel">
                    <div className="ql-orb">
                        <img src="/copyfilogo.png" alt="" />
                    </div>
                    {/* Skip on the counter's line, after it: floating in the corner it
                        sat on top of "1 / 5" (Guillaume, 2026-10-09). */}
                    <div className="ql-eyebrow">
                        <span>{tr(step.sec)}</span>
                        <span className="ql-eyebrow-end">
                            <span className="ql-idx">{secPos} / {siblings.length}</span>
                            <button type="button" className="ql-lnk ql-skip" onClick={endTour}>{t("tour.skip")}</button>
                        </span>
                    </div>
                    <h4 className="ql-title">{tr(step.title)}</h4>
                    <p className="ql-body">{tr(step.body)}</p>

                    <div className="ql-foot">
                        <div className="ql-dots">
                            {siblings.map((j) => (
                                <i key={j} className={j === idx ? "on" : j < idx ? "past" : ""} />
                            ))}
                        </div>
                        <div className="ql-acts">
                            {idx > 0 && (
                                <button type="button" className="ql-lnk" onClick={() => goTo(idx - 1, -1)}>
                                    {t("tour.back")}
                                </button>
                            )}
                            <button type="button" className="ql-next" onClick={() => goTo(idx + 1, 1)}>
                                {step.next ? tr(step.next) : (idx === steps.length - 1 ? t("tour.done") : t("tour.next"))}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

const TOUR_CSS = `
.ql-spot {
  position: fixed; z-index: 100; border-radius: 12px; pointer-events: none;
  transition: top .45s cubic-bezier(.3,.9,.25,1), left .45s cubic-bezier(.3,.9,.25,1),
              width .45s cubic-bezier(.3,.9,.25,1), height .45s cubic-bezier(.3,.9,.25,1);
  box-shadow:
    0 0 0 1px rgba(120,180,255,0.22),
    0 0 24px 4px rgba(59,130,246,0.12),
    0 0 0 9999px rgba(4,6,10,0.76);
}
.ql-spot::after {
  content: ""; position: absolute; inset: -3px; border-radius: 15px;
  border: 1px solid rgba(96,165,250,0.18);
  animation: ql-breathe 3.4s ease-in-out infinite;
}
@keyframes ql-breathe {
  0%, 100% { opacity: .35; transform: scale(1); }
  50%      { opacity: .9;  transform: scale(1.012); }
}
.ql-dim { position: fixed; inset: 0; z-index: 100; background: rgba(4,6,10,0.8); pointer-events: none; }
.ql-catch { position: fixed; inset: 0; z-index: 101; }

.ql-coach {
  position: fixed; top: 0; left: 0; z-index: 110;
  width: min(360px, calc(100vw - 24px));
  transition: transform .5s cubic-bezier(.32,.86,.28,1), opacity .25s ease;
}
.ql-coach.ql-center { width: min(420px, calc(100vw - 24px)); }
.ql-panel {
  position: relative;
  background: linear-gradient(180deg, rgba(24,29,42,0.97), rgba(17,21,31,0.97));
  border: 1px solid rgba(96,165,250,0.22);
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(0,0,0,0.62), 0 0 0 1px rgba(0,0,0,0.4), 0 0 40px rgba(59,130,246,0.10);
  padding: 20px 20px 14px;
}
.ql-center .ql-panel { padding: 26px 26px 16px; }

.ql-orb {
  position: absolute; left: -21px; top: -23px;
  width: 54px; height: 54px; border-radius: 50%;
  display: grid; place-items: center;
  background: rgba(10,14,22,0.92);
  box-shadow: 0 0 0 1px rgba(96,165,250,0.25), 0 0 26px rgba(59,130,246,0.45);
  animation: ql-bob 3.6s ease-in-out infinite;
}
.ql-orb img { width: 44px; height: 44px; object-fit: contain; }
@keyframes ql-bob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-5px); }
}

.ql-eyebrow {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.1em;
  color: hsl(210 60% 70%); font-weight: 600; margin: 0 0 9px; padding-left: 26px;
}
.ql-center .ql-eyebrow { padding-left: 0; }
.ql-idx { color: hsl(215 15% 48%); font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: .04em; }
.ql-title {
  margin: 0 0 7px; font-size: 15.5px; font-weight: 600; letter-spacing: -0.015em;
  color: hsl(210 20% 96%); text-wrap: balance;
}
.ql-center .ql-title { font-size: 20px; margin-bottom: 10px; }
.ql-body { margin: 0; font-size: 13px; line-height: 1.62; color: hsl(210 16% 80%); }
.ql-center .ql-body { font-size: 13.5px; }

.ql-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-top: 17px; padding-top: 13px; border-top: 1px solid rgba(255,255,255,0.07);
}
.ql-dots { display: flex; gap: 5px; align-items: center; }
.ql-dots i { width: 5px; height: 5px; border-radius: 50%; background: rgba(255,255,255,0.18); transition: all .3s; }
.ql-dots i.on { background: hsl(210 80% 62%); width: 16px; border-radius: 3px; }
.ql-dots i.past { background: rgba(96,165,250,0.42); }
.ql-acts { display: flex; align-items: center; gap: 8px; }
.ql-lnk {
  background: none; border: none; cursor: pointer; font-family: inherit;
  color: hsl(215 15% 48%); font-size: 12px; padding: 4px 2px;
}
.ql-lnk:hover { color: hsl(210 20% 96%); }
.ql-eyebrow-end { display: inline-flex; align-items: center; gap: 12px; flex-shrink: 0; }
.ql-skip { text-transform: none; letter-spacing: normal; font-weight: 400; }
.ql-next {
  display: inline-flex; align-items: center; justify-content: center;
  height: 28px; padding: 0 13px; border-radius: 6px; border: none; cursor: pointer;
  background: hsl(210 85% 50%); color: #fff;
  font-size: 12px; font-weight: 500; font-family: inherit;
  transition: filter .18s;
}
.ql-next:hover { filter: brightness(1.12); }
.ql-next:focus-visible, .ql-lnk:focus-visible { outline: 2px solid hsl(210 80% 62%); outline-offset: 2px; }

@media (prefers-reduced-motion: reduce) {
  .ql-spot, .ql-coach { transition: none; }
  .ql-spot::after, .ql-orb { animation: none; }
}
`;
